import { useCallback, useRef, useState, useEffect } from "react";
import { Button, Input, Popover, PopoverContent, PopoverTrigger, toast } from "@altimateai/lego";
import { AddTagToNodeRequest, BaseTag, Tag } from "../types";
import { useTags } from "../useTags";
import { TagForm } from "./TagForm";
import { AddIcon, CheckIcon, SearchIcon } from "@/assets/icons";
import { useMutation } from "@tanstack/react-query";
import { addTagToNode } from "@/helpers/apis";
import { Spinner, Stack } from "@altimateai/lego";
import { isAdmin, isOwner } from "@/services/auth";

export const AddTag = ({
  entity,
  existingTagIds = [],
  onAddTag,
}: {
  entity: AddTagToNodeRequest["entity"];
  existingTagIds?: string[];
  onAddTag: (tag: BaseTag) => void;
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [mutatingTagId, setMutatingTagId] = useState<string | null>(null);
  const [successTagIds, setSuccessTagIds] = useState<string[]>([]);
  const {
    tags,
    hasNextPage,
    fetchNextPage,
    isFetching: isLoading,
    isFetchingNextPage,
  } = useTags({
    mode: "infinite",
    loadTags: isOpen,
  });
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      inputRef.current?.focus();
    }
  }, [isOpen]);

  const handleScroll = useCallback(() => {
    const container = scrollContainerRef.current;
    if (!container || !hasNextPage || isFetchingNextPage) return;

    const { scrollTop, scrollHeight, clientHeight } = container;
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      fetchNextPage();
    }
  }, [hasNextPage, fetchNextPage, isFetchingNextPage]);

  const { mutate } = useMutation({
    mutationFn: addTagToNode,
    onSuccess: async (_, variables) => {
      toast({ description: `${variables.tag_rk} added to ${entity.column_rk || entity.table_rk}` });
      setMutatingTagId(null);
      setSuccessTagIds(prev => [...prev, variables.tag_rk]);
      const tag = tags.find(tag => tag.rk === variables.tag_rk) || variables.tag;
      if (tag) {
        onAddTag(tag as BaseTag);
      }
    },
    onError: error => {
      setMutatingTagId(null);
      toast({ description: `Failed to add tag to node: ${(error as Error).message || error}` });
    },
  });

  const filteredTags = tags.filter(
    tag =>
      tag.rk.toLowerCase().includes(searchQuery.toLowerCase()) && !existingTagIds.includes(tag.rk)
  );

  const handleCreateTag = () => {
    setShowForm(true);
  };

  const handleFormSubmit = async (data: Tag) => {
    setShowForm(false);
    setSearchQuery("");
    await mutate({ tag_rk: data.rk, entity, tag: data });
    setIsOpen(false);
  };

  const handleTagClick = (tag: Tag) => {
    setMutatingTagId(tag.rk);
    mutate({ tag_rk: tag.rk, entity });
  };

  const handleOpenChange = async (open: boolean) => {
    setIsOpen(open);
    if (!open) {
      setSearchQuery("");
      setMutatingTagId(null);
      setSuccessTagIds([]);
    }
  };

  if (!isOwner() && !isAdmin()) {
    return null;
  }

  return (
    <Popover open={isOpen} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="al-p-1 al-bg-gray-15 !al-rounded-full !al-p-1 !al-h-auto"
          onClick={e => e.stopPropagation()}
        >
          <AddIcon className="al-h-4 al-w-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="al-w-80 al-p-3" align="start">
        {showForm ? (
          <TagForm
            tag={{ display_name: searchQuery } as Tag}
            onSubmit={handleFormSubmit}
            onCancel={() => setShowForm(false)}
          />
        ) : (
          <>
            <div className="al-relative">
              <Input
                ref={inputRef}
                type="text"
                placeholder="Search tags..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="al-pl-8"
              />
              <SearchIcon className="al-absolute al-left-2 al-top-1/2 al--translate-y-1/2 al-h-4 al-w-4 al-text-gray-400" />
            </div>
            <div
              onScroll={handleScroll}
              ref={scrollContainerRef}
              className="al-max-h-48 al-overflow-y-auto al-mt-2"
            >
              {isLoading ? (
                <Stack justify="center" className="al-py-2">
                  <Spinner size="sm" />
                </Stack>
              ) : filteredTags.length > 0 ? (
                <>
                  {filteredTags.map(tag => (
                    <Button
                      key={tag.rk}
                      variant="ghost"
                      className="!al-justify-start al-px-2 al-py-1 hover:al-bg-gray-100 al-cursor-pointer al-rounded al-w-full"
                      onClick={e => {
                        e.stopPropagation();
                        handleTagClick(tag);
                      }}
                      disabled={mutatingTagId === tag.rk || successTagIds.includes(tag.rk)}
                    >
                      <Stack direction="row" align="center" gap={"sm"} className="al-w-full">
                        {mutatingTagId === tag.rk && <Spinner size="sm" className="al-mr-2" />}
                        {successTagIds.includes(tag.rk) && (
                          <CheckIcon className="al-h-4 al-w-4 al-mr-2" />
                        )}
                        {tag.rk}
                      </Stack>
                    </Button>
                  ))}
                  {isFetchingNextPage && (
                    <Stack justify="center" className="al-py-2">
                      <Spinner size="sm" />
                    </Stack>
                  )}
                </>
              ) : searchQuery ? (
                <div className="al-p-2">
                  <Button
                    variant="ghost"
                    className="al-w-full al-justify-start"
                    onClick={handleCreateTag}
                  >
                    + Create &quot;{searchQuery}&quot;
                  </Button>
                </div>
              ) : null}
            </div>
          </>
        )}
      </PopoverContent>
    </Popover>
  );
};
